import React, { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Bell, Target, Star } from 'lucide-react';
import { useUser } from '../../context/UserContext';
import WebPushService, { browserTimezone } from '../../utils/WebPushService';

export default function AthleteReminderSettings({ theme }) {
  const { user } = useUser();
  const [preferences, setPreferences] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const supported = WebPushService.isSupported();

  const currentUser = useRef(user.id);
  currentUser.current = user.id;
  const mounted = useRef(false);
  const changing = useRef(false);
  const reload = useRef(() => {});
  const loadVersion = useRef(0);
  const viewVersion = useRef(0);

  useEffect(() => {
    let active = true;
    viewVersion.current += 1;
    mounted.current = true;
    changing.current = false;
    setPreferences(null); setEnabled(false); setBusy(false); setMessage(''); setError('');
    const load = async () => {
      if (changing.current) return;
      const version = ++loadVersion.current;
      setEnabled(false);
      setError('');
      try {
        const data = await WebPushService.getSettings(user.id);
        if (!active || loadVersion.current !== version) return;
        setPreferences(data.preferences);
        const ready = supported && await WebPushService.isEnabled(user.id);
        if (active && loadVersion.current === version) setEnabled(ready);
      } catch (e) {
        if (active && loadVersion.current === version) setError(e.response?.data?.detail || 'Could not check reminder settings. Reconnect and retry.');
      }
    };
    reload.current = load;
    load();
    const events = ['mygoal-notifications-enabled', 'mygoal-reminders-updated', 'focus', 'online'];
    events.forEach(event => window.addEventListener(event, load));
    return () => {
      active = false; mounted.current = false;
      events.forEach(event => window.removeEventListener(event, load));
    };
  }, [user.id, supported]);

  const change = async (operation) => {
    const userId = user.id;
    const version = viewVersion.current;
    const active = () => mounted.current && currentUser.current === userId && viewVersion.current === version;
    if (changing.current) return;
    changing.current = true;
    loadVersion.current += 1;
    setBusy(true); setError(''); setMessage('');
    try {
      // Invoke before yielding so enable keeps Safari's direct user gesture.
      const result = await operation();
      if (active()) {
        if (result.preferences) setPreferences(result.preferences);
        if (typeof result.enabled === 'boolean') setEnabled(result.enabled);
        setMessage(result.message);
      }
    } catch (e) {
      if (active()) setError(e.response?.data?.detail || e.message || 'Could not save reminders. Please retry.');
    } finally {
      if (active()) { changing.current = false; setBusy(false); }
    }
  };
  const toggle = async (key) => {
    const next = { ...preferences, [key]: !preferences[key], timezone: browserTimezone() };
    const saved = await WebPushService.updatePreferences(user.id, next);
    return { preferences: saved || next, message: 'Reminder preferences saved.' };
  };

  return <section className={`p-5 rounded-2xl border mb-4 ${theme.card}`} data-testid="athlete-reminder-settings">
    <div className="flex items-center gap-3"><span className="rounded-xl bg-violet-500/15 p-2"><Bell size={20} className="text-violet-400" /></span><h2 className={`font-semibold ${theme.textPrimary}`}>Activity reminders</h2></div>
    <p className={`mt-3 text-sm ${theme.textSecondary}`}>A little help to prepare and reflect. Reminders stop once you have filled it in, or when you are not attending.</p>
    <div className="my-4 space-y-4">
      {[['goal_reminders', Target, 'Choose your goal', 'At 09:00 on your training day.'], ['rating_reminders', Star, 'Rate your activity', '30 minutes after your activity ends.']].map(([key, Icon, title, description]) =>
        <label key={key} className="flex items-center gap-3 cursor-pointer">
          <Icon size={19} className="text-violet-400 shrink-0" /><span className="flex-1"><span className={`block text-sm font-medium ${theme.textPrimary}`}>{title}</span><span className={`text-xs ${theme.textMuted}`}>{description}</span></span>
          <input type="checkbox" role="switch" aria-label={title} checked={Boolean(preferences?.[key])} disabled={!preferences || busy} onChange={() => change(() => toggle(key))} className="h-5 w-5 accent-violet-500" />
        </label>)}
    </div>
    <p className={`mb-3 text-xs ${theme.textMuted}`}>Times use {preferences?.timezone || browserTimezone()}. Notification previews do not include your goal or scores.</p>
    {!supported ? <p className={`text-sm ${theme.textSecondary}`}>{Capacitor.getPlatform() === 'android' ? 'Use the notification controls on Home to enable reminders on this device.' : 'On iPhone, use iOS 16.4 or later, add MyGoal to your Home Screen in Safari, and open it from that icon. On other devices, use a browser that supports push notifications.'}</p> :
      <div className="flex flex-wrap gap-2">
        <button disabled={busy || !preferences} onClick={() => change(async () => {
          if (enabled) {
            await WebPushService.disable(user.id);
            return { enabled: false, message: 'Reminders disabled on this device.' };
          }
          const saved = await WebPushService.enable(user.id, preferences);
          return { preferences: saved, enabled: true, message: 'Reminders enabled on this device.' };
        })} className="rounded-xl bg-[#493986] px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">{busy ? 'Saving…' : enabled ? 'Disable on this device' : 'Enable on this device'}</button>
        {enabled && <button disabled={busy} onClick={() => change(async () => { await WebPushService.test(user.id); return { message: 'Test notification sent.' }; })} className={`rounded-xl border px-4 py-3 text-sm ${theme.button}`}>Send test notification</button>}
      </div>}
    {enabled && <p className={`mt-3 text-xs ${theme.textSecondary}`}>Enabled on this device, including when the app is closed. Your device’s notification and Focus settings control alerts.</p>}
    {message && <p role="status" className={`mt-3 text-sm ${theme.textSecondary}`}>{message}</p>}
    {error && <div className="mt-3"><p role="alert" className="text-sm text-red-400">{error}</p><button disabled={busy} onClick={() => reload.current()} className={`mt-2 text-sm underline ${theme.textSecondary}`}>Retry reminder settings</button></div>}
  </section>;
}
